import { ICompany } from './CompaniesManagementPage';
import API from '../../../utils/axios.utils';

function getAuthHeaders() {
  const token = sessionStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Interface for company update data sent to the API
 */
type CompanyUpdateData = {
  name?: string;
  contact?: {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
  };
  address?: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
  };
  status?: boolean;
};

/**
 * API company data interface from the backend
 */
interface ApiCompanyData {
  edgelink_company_id: number;
  name: string;
  main_contact: string | null;
  main_contact_phone: string | null;
  main_contact_email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  status: boolean;
}

/**
 * Fetch all companies
 * @returns Promise with array of companies
 */
export const getCompanies = async (): Promise<ICompany[]> => {
  try {
    const response = await API.get<ApiCompanyData[]>('/api/admin/edgelink/companies/', {
      headers: getAuthHeaders(),
    });

    // Transform API response to match ICompany interface
    return response.data.map((company) => ({
      id: company.edgelink_company_id,
      name: company.name,
      contact: {
        name: company.main_contact,
        phone: company.main_contact_phone,
        email: company.main_contact_email,
      },
      address: {
        street: company.address,
        city: company.city,
        state: company.state,
        zip: company.zip,
        country: company.country,
      },
      status: company.status,
    }));
  } catch (error) {
    console.error('Error fetching companies:', error);
    return [];
  }
};

/**
 * Update company information
 * @param companyId - The company ID
 * @param data - The data to update
 * @returns Promise with updated company data
 */
export const updateCompany = async (companyId: number, data: CompanyUpdateData): Promise<ICompany | null> => {
  try {
    // Construct the API URL
    const url = `/api/admin/edgelink/companies/${companyId}/`;

    // Send PUT request to update company
    const response = await API.put(url, data, {
      headers: getAuthHeaders(),
    });

    // Transform the API response to match ICompany interface
    if (response.data) {
      const responseData = response.data as ApiCompanyData;
      return {
        id: responseData.edgelink_company_id,
        name: responseData.name,
        contact: {
          name: responseData.main_contact,
          phone: responseData.main_contact_phone,
          email: responseData.main_contact_email,
        },
        address: {
          street: responseData.address,
          city: responseData.city,
          state: responseData.state,
          zip: responseData.zip,
          country: responseData.country,
        },
        status: responseData.status,
      };
    }

    return null;
  } catch (error) {
    console.error('Error updating company:', error);
    throw error; // Throw the error to be handled by the component
  }
};

/**
 * Create a new company
 * @param data - The company data to create
 * @returns Promise with created company data
 */
export const createCompany = async (data: CompanyUpdateData): Promise<ICompany | null> => {
  try {
    // Construct the API URL
    const url = '/api/admin/edgelink/companies/';

    // Send POST request to create company
    const response = await API.post(url, data, {
      headers: getAuthHeaders(),
    });

    // Transform the API response to match ICompany interface
    if (response.data) {
      const responseData = response.data as ApiCompanyData;
      return {
        id: responseData.edgelink_company_id,
        name: responseData.name,
        contact: {
          name: responseData.main_contact,
          phone: responseData.main_contact_phone,
          email: responseData.main_contact_email,
        },
        address: {
          street: responseData.address,
          city: responseData.city,
          state: responseData.state,
          zip: responseData.zip,
          country: responseData.country,
        },
        status: responseData.status,
      };
    }

    return null;
  } catch (error) {
    console.error('Error creating company:', error);
    throw error; // Throw the error to be handled by the component
  }
};
